import 'css/src/components/common/dashboard-sidebar';

import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { bindActionCreators } from 'redux';
import type Luminary from '@lumen5/luminary';

import DashboardProjectItem from 'js/src/components/common/DashboardProjectItem';
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
    BUTTON_WIDTHS,
} from 'js/src/components/common/ui/Button';
import { getIsUserAllowedToManageProject, selectProjectIdsInWorkspace } from 'js/src/data/account';
import { resetSearchedVideos, selectActiveView, setActiveView } from 'js/src/data/dashboard';
import { setLastAccessedProject } from 'js/src/data/media/cookie-operations';
import { showModal } from 'js/src/data/modals';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { bindLuminaryOperations, luminaryReduxConnect } from 'js/src/luminary';
import {
    loadAllProjectsOperation,
    switchToVideoViewOperation,
} from 'js/src/luminary/dashboard-luminary-operations';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

type PropsFromRedux = ConnectedProps<typeof connector>;
type OwnProps = {
    onProjectAdd: VoidFunction;
};

const DashboardProjectListPresentation: React.FC<PropsFromRedux & OwnProps> = React.memo(
    function ProjectList({
        projectIds,
        onProjectAdd,
        activeView,
        userCanManageProjects,
        loadAllProjects,
        switchToVideoView,
    }) {
        // effect when loading to call loadProjects
        React.useEffect(() => {
            loadAllProjects();
        }, [loadAllProjects]);

        const setActiveProject = React.useCallback(
            (projectOrmId: number) => {
                switchToVideoView(projectOrmId);
                setLastAccessedProject(projectOrmId, SERVER.USER.workspace_id);
            },
            [switchToVideoView],
        );

        const projectAddButton = userCanManageProjects ? (
            <Button
                size={BUTTON_SIZES.SM}
                color={BUTTON_COLORS.TERTIARY}
                variant={BUTTON_VARIANTS.OUTLINE}
                width={BUTTON_WIDTHS.NO_MIN_WIDTH}
                text="Add"
                iconLeft="add"
                data-testid="add-project"
                onClick={onProjectAdd}
            />
        ) : null;

        const projects = projectIds.toJS().map((projectId) => {
            return (
                <DashboardProjectItem
                    activeView={activeView}
                    onProjectClick={setActiveProject}
                    key={projectId}
                    projectId={projectId}
                />
            );
        });

        return (
            <>
                <li className="add-project d-flex justify-content-between" key="add-project">
                    <span className="my-l5-auto fw-semibold">PROJECTS</span>
                    {projectAddButton}
                </li>
                {projects}
            </>
        );
    },
);

const mapStateToProps = (state: RootState) => {
    return {
        projectIds: selectProjectIdsInWorkspace(state, SERVER.USER.workspace_id),
        activeView: selectActiveView(state),
        userCanManageProjects: getIsUserAllowedToManageProject(state),
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return bindActionCreators(
        {
            showModal,
            setActiveView,
            resetSearchedVideos,
        },
        dispatch,
    );
};

const mapLuminaryOperationsToProps = (
    dispatch: AppDispatch,
    getState: GetRootState,
    luminary: Luminary,
) => {
    return bindLuminaryOperations(
        {
            loadAllProjectsOperation,
            switchToVideoViewOperation,
        },
        dispatch,
        getState,
        luminary,
    );
};

const connector = luminaryReduxConnect(
    mapStateToProps,
    mapDispatchToProps,
    undefined,
    mapLuminaryOperationsToProps,
);

export const DashboardProjectList = connector(DashboardProjectListPresentation);
